import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { CardBrand } from "../components/CardBrand";
import { CardForm } from "../components/CardForm";
import { api, errMsg, newIdemKey, type Card, type Account, type AccountType, type CurrencySummary, type HistoryItem } from "../api";
import { Modal } from "../components/Modal";
import { Btn, Field, Input, Notice, PageHead, Select } from "../components/ui";
import { useEntities } from "../entity";
import { fmtDay, formatMoney, normalizeMoneyInput } from "../lib/money";
import { useMe } from "../session";

type Filter = "ativa" | "inativa" | "todas";
const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

/** Contas Financeiras (F2-E1): cadastro, edição, inativação e histórico. Saldos e totais vêm do backend. */
export function FinancialAccounts() {
  const { me } = useMe();
  const realm = me.user.realm;
  const { current, loading: loadingEntity } = useEntities();
  const [types, setTypes] = useState<AccountType[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [summary, setSummary] = useState<CurrencySummary[]>([]);
  const [filter, setFilter] = useState<Filter>("ativa");
  const [state, setState] = useState<"loading" | "ok" | "error">("loading");
  const [edit, setEdit] = useState<Account | "new" | null>(null);
  const [off, setOff] = useState<Account | null>(null);
  const [hist, setHist] = useState<Account | null>(null);
  const [cards, setCards] = useState<Card[]>([]);
  const [cardEdit, setCardEdit] = useState<Card | "new" | null>(null);
  const [cardOff, setCardOff] = useState<Card | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [topErr, setTopErr] = useState<string | null>(null);

  const groups = useMemo(() => {
    const m = new Map<string, Account[]>();
    for (const a of accounts) { if (!m.has(a.typeLabel)) m.set(a.typeLabel, []); m.get(a.typeLabel)!.push(a); }
    return [...m.entries()];
  }, [accounts]);
  const canWrite = current?.permission === "total";
  const load = useCallback(async () => {
    if (!current) return;
    try {
      const [a, s, cd] = await Promise.all([
        api<Account[]>("GET", `/api/${realm}/accounts?entityId=${current.id}${filter === "todas" ? "" : `&status=${filter}`}`),
        api<CurrencySummary[]>("GET", `/api/${realm}/accounts/summary?entityId=${current.id}`),
        api<Card[]>("GET", `/api/${realm}/cards?entityId=${current.id}${filter === "todas" ? "" : `&status=${filter}`}`),
      ]);
      setAccounts(a); setSummary(s); setCards(cd); setState("ok");
    } catch { setState("error"); }
  }, [realm, current, filter]);
  useEffect(() => { setState("loading"); void load(); }, [load]);
  useEffect(() => { void api<AccountType[]>("GET", `/api/${realm}/account-types`).then(setTypes).catch(() => undefined); }, [realm]);

  const done = (msg: string) => { setEdit(null); setOff(null); setCardEdit(null); setCardOff(null); setTopErr(null); setFlash(msg); void load(); };

  if (loadingEntity) return <div className="muted">Carregando…</div>;
  if (!current) return <Notice kind="error">Nenhuma entidade disponível para o seu usuário.</Notice>;

  return (
    <>
      <PageHead title="Contas Financeiras" sub={`${current.name} — cadastro das contas onde o dinheiro circula.`} />
      {flash && <Notice kind="ok">{flash}</Notice>}
      {topErr && <Notice kind="error">{topErr}</Notice>}
      <div className="summary" aria-label="Saldo por moeda">
        {summary.length === 0 && <div className="sum-card"><span className="muted">Saldo total</span><strong>—</strong></div>}
        {summary.map((s) => (
          <div className="sum-card" key={s.currency} data-summary={s.currency}>
            <span className="muted">Saldo total em {s.currency} · {s.count} {s.count === 1 ? "conta" : "contas"}</span>
            <strong className="tabular">{formatMoney(s.total.decimal, s.currency)}</strong>
            <span className="muted small">Previsto até o fim do mês: <span className="tabular" data-summary-projected>{formatMoney(s.projected.decimal, s.currency)}</span></span>
          </div>
        ))}
      </div>
      <div className="toolbar">
        <div className="seg" role="group" aria-label="Filtro de situação">
          {([["ativa", "Ativas"], ["inativa", "Inativas"], ["todas", "Todas"]] as const).map(([k, l]) => (
            <button key={k} aria-pressed={filter === k} onClick={() => setFilter(k)}>{l}</button>
          ))}
        </div>
        <span className="spacer" />
        {canWrite && <Btn variant="ghost" onClick={() => { setFlash(null); setCardEdit("new"); }} data-new-card>+ Novo cartão</Btn>}
        {canWrite && <Btn onClick={() => { setFlash(null); setEdit("new"); }} data-new-account>+ Nova conta</Btn>}
      </div>
      {!canWrite && <Notice>Você tem acesso somente leitura a esta entidade.</Notice>}
      {state === "error" && <Notice kind="error">Não foi possível carregar as contas. Tente novamente.</Notice>}
      {state === "ok" && accounts.length === 0 && cards.length === 0 && (
        <div className="card stub">
          <h3>{filter === "inativa" ? "Nenhuma conta inativa" : "Nenhuma conta cadastrada"}</h3>
          <p className="muted" style={{ margin: 0 }}>{canWrite && filter !== "inativa" ? "Cadastre a primeira conta para começar a organizar seus saldos." : "Não há contas para exibir neste filtro."}</p>
        </div>
      )}
      {(accounts.length > 0 || cards.length > 0) && (
        <div className="card table-card">
          <table aria-label="Contas financeiras">
            <thead><tr><th>Conta</th><th>Tipo</th><th>Moeda</th><th className="num">Saldo atual / previsto</th><th>Situação</th><th aria-label="Ações" /></tr></thead>
            <tbody>
              {groups.map(([label, list]) => [
                <tr key={"g" + label} className="group-row" data-group={label}><th colSpan={6}>{label} ({list.length})</th></tr>,
                ...list.map((a) => (
                <tr key={a.id} className={a.status === "inativa" ? "inactive" : ""} data-account={a.name}>
                  <td><strong>{a.name}</strong>{a.institution && <div className="muted small">{a.institution}</div>}</td>
                  <td>{a.typeLabel}</td>
                  <td>{a.currency}</td>
                  <td className="num tabular">{formatMoney(a.balance.decimal, a.currency)}<div className="muted small">Previsto: {formatMoney(a.projectedBalance.decimal, a.currency)}</div><div className="muted small">Saldo inicial em {fmtDay(a.initialBalanceDate)}: {formatMoney(a.initialBalance.decimal, a.currency)}</div></td>
                  <td><span className={`pill ${a.status === "ativa" ? "accent" : ""}`}>{a.status === "ativa" ? "Ativa" : "Inativa"}</span>{a.inactivationReason && <div className="muted small">{a.inactivationReason}</div>}</td>
                  <td className="actions">
                    {canWrite && a.status === "ativa" && <><button className="link" onClick={() => { setFlash(null); setEdit(a); }}>Editar</button><button className="link danger" onClick={() => { setFlash(null); setOff(a); }}>Inativar</button></>}
                    {canWrite && a.status === "inativa" && <button className="link" onClick={() => void reactivate(a)}>Reativar</button>}
                    <button className="link" onClick={() => setHist(a)}>Histórico</button>
                  </td>
                </tr>
                )),
              ])}
              {cards.length > 0 && <tr className="group-row" data-group="Cartão de crédito"><th colSpan={6}>Cartão de crédito ({cards.length})</th></tr>}
              {cards.map((c) => (
                <tr key={c.id} className={c.status === "inativa" ? "inactive" : ""} data-card={c.name}>
                  <td><div className="name-with-brand"><CardBrand brand={c.brand} /><div><strong>{c.name}</strong>{c.institution && <div className="muted small">{c.institution}</div>}</div></div></td>
                  <td>Cartão de crédito</td>
                  <td>BRL</td>
                  <td className="num tabular">Limite: {formatMoney(c.creditLimit.decimal, "BRL")}<div className="muted small">Vencimento: dia {c.dueDay} · Fechamento: dia {c.closingDay}</div><div className="muted small">Utilizado: {formatMoney(c.used.decimal, "BRL")} · Disponível: {formatMoney(c.available.decimal, "BRL")}</div></td>
                  <td><span className={`pill ${c.status === "ativa" ? "accent" : ""}`}>{c.status === "ativa" ? "Ativo" : "Inativo"}</span>{c.inactivationReason && <div className="muted small">{c.inactivationReason}</div>}</td>
                  <td className="actions">
                    <Link className="link" to={`/${realm}/cartoes?card=${c.id}`}>Fatura</Link>
                    {canWrite && c.status === "ativa" && <><button className="link" onClick={() => { setFlash(null); setCardEdit(c); }}>Editar</button><button className="link danger" onClick={() => { setFlash(null); setCardOff(c); }} data-card-off>Inativar</button></>}
                    {canWrite && c.status === "inativa" && <button className="link" onClick={() => void reactivateCard(c)}>Reativar</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {cardEdit && <CardForm key={cardEdit === "new" ? "new" : cardEdit.id} entityId={current.id} card={cardEdit === "new" ? null : cardEdit} onClose={() => setCardEdit(null)} onDone={(m) => done(m)} />}
      {edit && <AccountForm key={edit === "new" ? "new" : edit.id} entityId={current.id} account={edit === "new" ? null : edit} types={types} onClose={() => setEdit(null)} onDone={done} />}
      {cardOff && <CardInactivateForm card={cardOff} onClose={() => setCardOff(null)} onDone={done} />}
      {off && <InactivateForm account={off} onClose={() => setOff(null)} onDone={done} />}
      {hist && <History account={hist} onClose={() => setHist(null)} />}
    </>
  );

  async function reactivateCard(c: Card) {
    try { await api("POST", `/api/${realm}/cards/${c.id}/reactivate`, { version: c.version }); done(`Cartão "${c.name}" reativado.`); }
    catch (e) { setFlash(null); setTopErr(errMsg(e)); void load(); }
  }
  async function reactivate(a: Account) {
    try { await api("POST", `/api/${realm}/accounts/${a.id}/reactivate`, { version: a.version }); done(`Conta "${a.name}" reativada.`); }
    catch (e) { setFlash(null); setTopErr(errMsg(e)); void load(); }
  }
}

function AccountForm({ entityId, account, types, onClose, onDone }: { entityId: string; account: Account | null; types: AccountType[]; onClose: () => void; onDone: (m: string) => void }) {
  const { me } = useMe();
  const realm = me.user.realm;
  const [name, setName] = useState(account?.name ?? "");
  const [type, setType] = useState(account?.type ?? "conta_corrente");
  const [currency, setCurrency] = useState<"BRL" | "USD">(account?.currency ?? "BRL");
  const [institution, setInstitution] = useState(account?.institution ?? "");
  const [balance, setBalance] = useState(account ? account.initialBalance.decimal.replace(".", ",") : "0,00");
  const [date, setDate] = useState(account?.initialBalanceDate ?? today());
  const [overdraft, setOverdraft] = useState(account ? account.overdraftLimit.decimal.replace(".", ",") : "0,00");
  const [notes, setNotes] = useState(account?.notes ?? "");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const idem = useMemo(() => newIdemKey(), []);   // mesma tentativa => mesma chave (reenvio seguro)
  const available = types.filter((t) => t.available);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    const norm = normalizeMoneyInput(balance);
    if (norm === null) return setErr("Saldo inicial inválido. Use números com até 2 casas decimais, por exemplo 1.234,56.");
    let limitNorm: string | null = "0";
    if (type === "conta_corrente") {
      limitNorm = normalizeMoneyInput(overdraft || "0");
      if (limitNorm === null || limitNorm.startsWith("-")) return setErr("Limite da conta inválido. Use um valor maior ou igual a zero, por exemplo 1.500,00.");
    }
    setBusy(true);
    try {
      const body = { name, type, currency, institution: institution.trim() || null, notes: notes.trim() || null, initialBalance: norm, initialBalanceDate: date, overdraftLimit: limitNorm };
      if (account) {
        await api("PATCH", `/api/${realm}/accounts/${account.id}`, { version: account.version, ...body });
        onDone(`Conta "${name.trim()}" atualizada.`);
      } else {
        await api("POST", `/api/${realm}/accounts`, { entityId, ...body }, idem);
        onDone(`Conta "${name.trim()}" criada.`);
      }
    } catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  }

  return (
    <Modal title={account ? "Editar conta" : "Nova conta"} onClose={onClose}>
      <form onSubmit={submit} noValidate>
        {err && <Notice kind="error">{err}</Notice>}
        <Field label="Nome da conta"><Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} required autoComplete="off" name="name" /></Field>
        <div className="grid2">
          <Field label="Tipo"><Select value={type} onChange={(e) => setType(e.target.value)} name="type">{available.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}</Select></Field>
          <Field label="Moeda"><Select value={currency} onChange={(e) => setCurrency(e.target.value as "BRL" | "USD")} name="currency"><option value="BRL">Real (BRL)</option><option value="USD">Dólar (USD)</option></Select></Field>
        </div>
        <Field label="Instituição (opcional)"><Input value={institution} onChange={(e) => setInstitution(e.target.value)} maxLength={120} name="institution" /></Field>
        <div className="grid2">
          <Field label="Saldo inicial"><Input value={balance} onChange={(e) => setBalance(e.target.value)} inputMode="decimal" decimals={2} negative name="initialBalance" /></Field>
          <Field label="Data do saldo inicial"><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} name="initialBalanceDate" required /></Field>
        </div>
        {type === "conta_corrente" && <Field label="Limite da conta (cheque especial)"><Input value={overdraft} onChange={(e) => setOverdraft(e.target.value)} inputMode="decimal" decimals={2} name="overdraftLimit" placeholder="0,00" /></Field>}
        <Field label="Observações (opcional)"><Input value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} name="notes" /></Field>
        <div className="modal-actions"><Btn variant="ghost" onClick={onClose}>Cancelar</Btn><button type="submit" className="btn primary" disabled={busy}>{busy ? "Salvando…" : "Salvar"}</button></div>
      </form>
    </Modal>
  );
}

function CardInactivateForm({ card, onClose, onDone }: { card: Card; onClose: () => void; onDone: (m: string) => void }) {
  const { me } = useMe();
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (reason.trim().length < 3) return setErr("Informe o motivo (mínimo de 3 letras).");
    setBusy(true); setErr(null);
    try { await api("POST", `/api/${me.user.realm}/cards/${card.id}/inactivate`, { version: card.version, reason: reason.trim() }); onDone(`Cartão "${card.name}" inativado. O histórico foi preservado.`); }
    catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  }
  return (
    <Modal title="Inativar cartão" onClose={onClose}>
      <form onSubmit={submit} noValidate>
        {err && <Notice kind="error">{err}</Notice>}
        <p style={{ marginTop: 0 }}>O cartão <strong>{card.name}</strong> deixará de aceitar novas compras. Só é possível inativar um cartão sem fatura em aberto. O histórico é preservado e você pode reativá-lo depois.</p>
        <Field label="Motivo da inativação"><Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} required name="reason" autoComplete="off" /></Field>
        <div className="modal-actions"><Btn variant="ghost" onClick={onClose}>Cancelar</Btn><button type="submit" className="btn primary" disabled={busy}>{busy ? "Inativando…" : "Inativar cartão"}</button></div>
      </form>
    </Modal>
  );
}

function InactivateForm({ account, onClose, onDone }: { account: Account; onClose: () => void; onDone: (m: string) => void }) {
  const { me } = useMe();
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try { await api("POST", `/api/${me.user.realm}/accounts/${account.id}/inactivate`, { version: account.version, reason }); onDone(`Conta "${account.name}" inativada. O histórico foi preservado.`); }
    catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  }
  return (
    <Modal title="Inativar conta" onClose={onClose}>
      <form onSubmit={submit} noValidate>
        {err && <Notice kind="error">{err}</Notice>}
        <p style={{ marginTop: 0 }}>A conta <strong>{account.name}</strong> deixará de aceitar novas movimentações. O histórico e o saldo são preservados e você pode reativá-la depois.</p>
        <Field label="Motivo da inativação"><Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} required name="reason" autoComplete="off" /></Field>
        <div className="modal-actions"><Btn variant="ghost" onClick={onClose}>Cancelar</Btn><button type="submit" className="btn primary" disabled={busy}>{busy ? "Inativando…" : "Inativar conta"}</button></div>
      </form>
    </Modal>
  );
}

const ACTION: Record<string, string> = {
  "finance.account_created": "Conta criada", "finance.account_updated": "Conta alterada",
  "finance.account_inactivated": "Conta inativada", "finance.account_reactivated": "Conta reativada",
};
const FIELD: Record<string, string> = {
  name: "Nome", type: "Tipo", currency: "Moeda", institution: "Instituição", notes: "Observações",
  initial_balance: "Saldo inicial", initial_balance_date: "Data do saldo inicial", status: "Situação",
};

function History({ account, onClose }: { account: Account; onClose: () => void }) {
  const { me } = useMe();
  const [items, setItems] = useState<HistoryItem[] | null>(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api<HistoryItem[]>("GET", `/api/${me.user.realm}/accounts/${account.id}/history`).then(setItems).catch(() => setErr(true)); }, [me.user.realm, account.id]);
  return (
    <Modal title={`Histórico — ${account.name}`} onClose={onClose} wide>
      {err && <Notice kind="error">Não foi possível carregar o histórico.</Notice>}
      {!items && !err && <p className="muted">Carregando…</p>}
      {items && (
        <ol className="history">
          {items.map((h) => (
            <li key={h.id}>
              <div><strong>{ACTION[h.action] ?? h.action}</strong> <span className="muted small">· {new Date(h.at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}{h.actor ? ` · ${h.actor}` : ""}</span></div>
              {h.details.motivo && <div className="small">Motivo: {h.details.motivo}</div>}
              {h.details.changes && Object.entries(h.details.changes as Record<string, { de: unknown; para: unknown }>).map(([k, v]) => (
                <div className="small change" key={k}>{FIELD[k] ?? k}: <span className="old">{String(v.de ?? "—")}</span> → <span className="new">{String(v.para ?? "—")}</span></div>
              ))}
              {h.details.depois && <div className="small muted">Saldo inicial {String((h.details.depois as any).initial_balance)} {String((h.details.depois as any).currency)} em {fmtDay(String((h.details.depois as any).initial_balance_date))}</div>}
            </li>
          ))}
        </ol>
      )}
    </Modal>
  );
}
